import { useMemo, useState } from 'react';
import { useMenusContext } from 'root/contexts/MenusContext';
import { useGetDisplaySettingsParams } from 'root/hooks/useGetDisplaySettingsParams';
import type { GalleryImage } from '@wix/simple-gallery';
import type { ItemImage } from 'root/apiTypes';
import { getImageSize } from 'root/utils/images';

interface ImageLogicProps<T extends ItemImage> {
  image: T;
  additionalImages: T[];
  onExpand: () => void;
}

export const ImageLogic = <T extends ItemImage>({ image, additionalImages, onExpand }: ImageLogicProps<T>) => {
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const { isMobile } = useMenusContext();
  const { showItemExpandedImage, showItemMultiImagesGallery } = useGetDisplaySettingsParams();

  const allImages: GalleryImage[] = useMemo(() => {
    const images: GalleryImage[] = [];
    if (image) {
      const imageSize = getImageSize(image);
      images.push({
        id: image.id || '',
        src: image.url || '',
        sourceHeight: imageSize.height,
        sourceWidth: imageSize.width,
      });
    }
    if (additionalImages && showItemMultiImagesGallery) {
      additionalImages.forEach((currentImage) => {
        const imageSize = getImageSize(currentImage);
        images.push({
          id: currentImage.id || '',
          src: currentImage.url || '',
          sourceHeight: imageSize.height,
          sourceWidth: imageSize.width,
        });
      });
    }
    return images;
  }, [additionalImages, image, showItemMultiImagesGallery]);

  const displayedImage: GalleryImage = useMemo(() => {
    if (isImageHovered) {
      return allImages[1];
    }
    return allImages[0];
  }, [allImages, isImageHovered]);

  const shouldShowMultiImagesIndication = useMemo(() => {
    return allImages.length > 1 && showItemMultiImagesGallery && isMobile;
  }, [allImages, showItemMultiImagesGallery, isMobile]);

  const shouldUseHoverEffect = useMemo(() => {
    return !isMobile && showItemMultiImagesGallery && allImages.length > 1;
  }, [isMobile, showItemMultiImagesGallery, allImages]);

  const onImageClick = () => {
    if (showItemExpandedImage) {
      setIsGalleryOpen(true);
      onExpand();
    }
  };

  const onImageHover = () => {
    if (shouldUseHoverEffect) {
      setIsImageHovered(true);
    }
  };

  const onImageLeave = () => {
    if (shouldUseHoverEffect) {
      setIsImageHovered(false);
    }
  };

  return {
    allImages,
    onImageClick,
    displayedImage,
    isGalleryOpen,
    setIsGalleryOpen,
    isMobile,
    isImageClickable: showItemExpandedImage,
    shouldShowMultiImagesIndication,
    onImageHover,
    onImageLeave,
  };
};
